// Design tokens — TS source of truth.
//
// Two exports, with different jobs:
//
//   `palette`  the MUI theme palette. Change a brand/semantic colour HERE and
//              both apps follow. Consumed only by buildMuiTheme().
//   `colors`   the CSS-variable mirror (see generate-css.mjs) plus the legacy
//              JS constants still referenced across both apps.
//
// They are separate because `colors.primary.*` etc. are still MUI's *default*
// palette, not this app's — `colors.primary.main` is #1976d2, which is NOT
// `palette.light.primary.main`. Merging them means repointing ~22 CSS lines and
// ~629 JS refs, which is its own migration. Until then, reach for `palette` (or
// `theme.palette`) for anything theme-related.
//
// NOTE: this file must stay import-free — generate-css.mjs loads it via Node's
// type-stripping, which cannot resolve imports.

/**
 * Copied from the designer's RubiTHEME Builder export (received 2026-10-27,
 * superseding the 2026-10-26 export). The export writes every value out in
 * full, so each colour channel here carries all four of `main`, `light`,
 * `dark` and `contrastText` and MUI derives nothing. Edit a shade here, not by
 * re-seeding `main`. The export's `common`, `grey`, `background`, `text` and
 * `divider` are MUI's defaults, so they are not repeated.
 *
 * The `status*` channels colour the report statuses. They are custom palette
 * slots, declared to TypeScript in ./theme.ts.
 *
 * `frame` paints the application shell: the top bar, icon rail and nav panel.
 * The shell sits outside the content area and stays dark in both modes, so it
 * has its own small palette rather than following `background.paper`. Each nav
 * item state is a `{ main, contrastText }` pair: `main` paints the item and
 * `contrastText` is the ink for its text and icons alike. Translucent states
 * carry their own alpha.
 *
 * Deliberately not taken from that export:
 * - `spacing: 12` / `typography.fontSize: 16`: a repo-wide relayout and a 14%
 *   type-scale change, not palette changes.
 * - The `rubiOG` scheme: neither app can select a third scheme yet.
 * - `cssVariables` / `colorSchemes` / `defaultColorScheme: "dark"`: both apps
 *   build one theme per mode from their colour-mode contexts, and keep their
 *   current default mode.
 */
export const palette = {
  light: {
    primary: {
      main: "#1a5cf4",
      light: "#477cf6",
      dark: "#1240aa",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#969696",
      light: "#ababab",
      dark: "#696969",
      contrastText: "#ffffff",
    },
    error: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#fff",
    },
    warning: {
      main: "#ff7b0f",
      light: "#ff953f",
      dark: "#b2560a",
      contrastText: "#ffffff",
    },
    info: {
      main: "#2e9ee8",
      light: "#57b1ec",
      dark: "#206ea2",
      contrastText: "#ffffff",
    },
    success: {
      main: "#5dab5e",
      light: "#7dbb7e",
      dark: "#417741",
      contrastText: "#ffffff",
    },
    statusDraft: {
      main: "#ebebeb",
      light: "#efefef",
      dark: "#a4a4a4",
      contrastText: "rgba(0, 0, 0, 0.87)",
    },
    statusSubmitted: {
      main: "#2e9ee8",
      light: "#57b1ec",
      dark: "#206ea2",
      contrastText: "#ffffff",
    },
    statusResubmitted: {
      main: "#6d7db9",
      light: "#8a97c7",
      dark: "#4c5781",
      contrastText: "#fff",
    },
    statusModificationRequested: {
      main: "#ed6c02",
      light: "#f08934",
      dark: "#a54b01",
      contrastText: "#fff",
    },
    statusModificationOpted: {
      main: "#ed6c02",
      light: "#f08934",
      dark: "#a54b01",
      contrastText: "#fff",
    },
    statusApproved: {
      main: "#5dab5e",
      light: "#7dbb7e",
      dark: "#417741",
      contrastText: "#ffffff",
    },
    statusRejected: {
      main: "#d32f2f",
      light: "#db5858",
      dark: "#932020",
      contrastText: "#fff",
    },
    statusEscalated: {
      main: "#495dab",
      light: "#6d7dbb",
      dark: "#334177",
      contrastText: "#fff",
    },
    statusPendingDeletion: {
      main: "#d32f2f",
      light: "#db5858",
      dark: "#932020",
      contrastText: "#fff",
    },
    statusPendingDeletionPublic: {
      main: "#d32f2f",
      light: "#db5858",
      dark: "#932020",
      contrastText: "#fff",
    },
    frame: {
      bg: "#1a1b2c",
      border: "rgba(255, 255, 255, 0.23)",
      text: {
        primary: "rgba(255, 255, 255, 0.87)",
        secondary: "rgba(255, 255, 255, 0.7)",
      },
      appsButton: "#ffffff",
      navItem: {
        main: "rgba(0, 0, 0, 0)",
        contrastText: "#fff",
        hover: { main: "rgba(255, 255, 255, 0.14)", contrastText: "#fff" },
        selected: { main: "#1a5cf4", contrastText: "#fff" },
        focus: { main: "rgba(255, 255, 255, 0.14)", contrastText: "#fff" },
        pressed: { main: "rgba(255, 255, 255, 0.14)", contrastText: "#fff" },
        disabled: {
          main: "rgba(255, 255, 255, 0.0)",
          contrastText: "rgba(254, 254, 254, 0.14)",
        },
      },
    },
  },
  dark: {
    primary: {
      main: "#3472fd",
      light: "#5c8efd",
      dark: "#244fb1",
      contrastText: "#fff",
    },
    secondary: {
      main: "#424242",
      light: "#676767",
      dark: "#2e2e2e",
      contrastText: "#fff",
    },
    error: {
      main: "#f44336",
      light: "#e57373",
      dark: "#d32f2f",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#f98c10",
      light: "#faa33f",
      dark: "#f08000",
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288d1",
      light: "#349fda",
      dark: "#0480c3",
      contrastText: "#fcfcfc",
    },
    success: {
      main: "#66bb6a",
      light: "#81c784",
      dark: "#388e3c",
      contrastText: "#ffffff",
    },
    statusDraft: {
      main: "#343434",
      light: "#5c5c5c",
      dark: "#242424",
      contrastText: "#fff",
    },
    statusSubmitted: {
      main: "#0288d1",
      light: "#349fda",
      dark: "#015f92",
      contrastText: "#fff",
    },
    statusResubmitted: {
      main: "#6d7db9",
      light: "#8a97c7",
      dark: "#4c5781",
      contrastText: "#fff",
    },
    statusModificationRequested: {
      main: "#ed6c02",
      light: "#f08934",
      dark: "#a54b01",
      contrastText: "#ffffff",
    },
    statusModificationOpted: {
      main: "#ed6c02",
      light: "#f08934",
      dark: "#a54b01",
      contrastText: "#ffffff",
    },
    statusApproved: {
      main: "#66bb6a",
      light: "#84c887",
      dark: "#47824a",
      contrastText: "#ffffff",
    },
    statusRejected: {
      main: "#f44336",
      light: "#f6685e",
      dark: "#aa2e25",
      contrastText: "#fff",
    },
    statusEscalated: {
      main: "#495dab",
      light: "#6d7dbb",
      dark: "#334177",
      contrastText: "#fff",
    },
    statusPendingDeletion: {
      main: "#f44336",
      light: "#f6685e",
      dark: "#aa2e25",
      contrastText: "#fff",
    },
    statusPendingDeletionPublic: {
      main: "#f44336",
      light: "#f6685e",
      dark: "#aa2e25",
      contrastText: "#fff",
    },
    frame: {
      bg: "#1f1f1f",
      border: "rgba(255, 255, 255, 0.14)",
      text: {
        primary: "#fff",
        secondary: "rgba(255, 255, 255, 0.7)",
      },
      appsButton: "#ffffff",
      navItem: {
        main: "rgba(0, 0, 0, 0)",
        contrastText: "#fff",
        hover: { main: "rgba(255, 255, 255, 0.07)", contrastText: "#fff" },
        selected: { main: "#0f57ff", contrastText: "#fff" },
        focus: { main: "rgba(255, 255, 255, 0.14)", contrastText: "#fff" },
        pressed: { main: "rgba(255, 255, 255, 0.14)", contrastText: "#fff" },
        disabled: {
          main: "rgba(0, 0, 0, 0)",
          contrastText: "rgba(255, 255, 255, 0.14)",
        },
      },
    },
  },
} as const;

/** A report-status channel in `palette`, usable as a Chip or Button `color`. */
export type StatusChannel = Extract<
  keyof typeof palette.light,
  `status${string}`
>;

/** Shape and tonal settings from the same config. All match MUI's defaults
 * today, but they are stated so that editing one has an effect. */
export const shape = { borderRadius: 4 } as const;
export const tonal = { tonalOffset: 0.2, contrastThreshold: 3 } as const;

export const colors = {
  brand: {
    500: palette.light.primary.main,
  },
  neutral: {
    0: "#ffffff",
    50: "#fafafb",
  },
  semantic: {
    danger: "#d32f2f",
    success: "#2e7d32",
    warning: "#ed6c02",
    info: "#0288d1",
  },
  // Off-palette colours with no slot in theme.config.json. Quarantined here
  // rather than inlined at call sites, per docs/frontend/CONVENTIONS.md.
  legacy: {
    coral: "#e74c3c",
    /** Portal chrome indigo — `MUI_TEXT_SECONDARY` in portal's common/colors.ts. */
    indigo: "#495dab",
  },
  primary: {
    light: "#42a5f5",
    main: "#1976d2",
    dark: "#1565c0",
    contrastText: "#ffffff",
  },
  secondary: {
    light: "#ba68c8",
    main: "#9c27b0",
    dark: "#7b1fa2",
    contrastText: "#ffffff",
  },
  error: {
    light: "#ef5350",
    main: "#d32f2f",
    dark: "#c62828",
    contrastText: "#ffffff",
  },
  warning: {
    light: "#ff9800",
    main: "#ed6c02",
    dark: "#e65100",
    contrastText: "#ffffff",
  },
  info: {
    light: "#03a9f4",
    main: "#0288d1",
    dark: "#01579b",
    contrastText: "#ffffff",
  },
  success: {
    light: "#4caf50",
    main: "#2e7d32",
    dark: "#1b5e20",
    contrastText: "#ffffff",
  },
  text: {
    primary: "#000000de",
    secondary: "#00000099",
    disabled: "#00000061",
  },
  divider: "#0000001f",
  header: "#1a1b2b", // dark nav/header surface (was inline #1A1B2B pre-tokens)
  background: {
    default: "#ffffff",
    paper: "#ffffff",
  },
  common: {
    black: "#000000",
    white: "#ffffff",
  },
  action: {
    active: "#0000008a",
    hover: "#0000000a",
    selected: "#00000014",
    disabled: "#00000042",
    disabledBackground: "#0000001f",
    focus: "#0000001f",
  },
  highlight: {
    full: "#ffabab", // HighlightText exact-match background
    partial: "#fff5ba", // HighlightText partial-match background
  },
} as const;

export const spacing = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

export const radii = {
  sm: 4,
  md: 8,
  lg: 16,
  full: 9999,
} as const;

export const typography = {
  // MUI's default stack — what portal renders. Services overlays Open Sans.
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  weights: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
} as const;
